import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { useAdminApi } from "./useAdminApi";
import { PanelHeader } from "./LeadsPanel";
import InstagramManager from "./InstagramManager";
import { API, imgUrl } from "@/lib/api";

export default function GalleryPanel() {
  const api = useAdminApi();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const load = () => axios.get(`${API}/gallery`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    setBusy(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: await api.headers() });
        await api.post("/admin/gallery", { url: data.url, caption: file.name.replace(/\.[^.]+$/, "") });
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || "upload failed"}`);
      }
    }
    toast.success("Gallery updated");
    setBusy(false);
    load();
  };

  const remove = async (g) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    await api.del(`/admin/gallery/${g.id}`);
    load();
  };

  return (
    <div data-testid="gallery-panel">
      <PanelHeader title="Gallery" sub="Upload photos (e.g. saved from your Facebook page). They appear first on the website Gallery page.">
        <button onClick={() => fileRef.current.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#00C9B7] text-[#031312] px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#00E0CC] disabled:opacity-60" data-testid="gallery-upload-btn">{busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} Upload photos</button>
      </PanelHeader>
      <InstagramManager />
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload([...e.target.files])} data-testid="gallery-file-input" />
      {items === null ? <p className="text-slate-500">Loading…</p> : items.length === 0 ? <p className="text-slate-500 py-16 text-center border border-dashed border-white/10 rounded-2xl">No uploaded photos yet. Built-in clinic photos are already shown on the site.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {items.map((g) => (
            <div key={g.id} className="relative group rounded-2xl overflow-hidden border border-white/10" data-testid={`gallery-admin-item-${g.id}`}>
              <img src={imgUrl(g.url)} alt={g.caption} className="w-full aspect-square object-cover" />
              <button onClick={() => remove(g)} className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white" data-testid={`gallery-admin-delete-${g.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
